import algoliasearch, { QueryParameters } from "algoliasearch";
import { DependencyList, useEffect, useState } from "react";
import environment from "src/environment";

import { useIsMountedFunctions } from "./useIsMountedFunctions";

export const algoliaClient = algoliasearch(
  environment.ALGOLIA.ID,
  environment.ALGOLIA.KEY,
  { timeout: 4000 }
);

type IInputFacets = string[];

interface IProps {
  indexName: string;
  query?: string;
  facets?: IInputFacets;
  facetFilters?: Array<string | string[]>;
  numericFilters?: string[];
  page?: number;
  hitsPerPage?: number;
  offset?: number;
  length?: number;
  maxValuesPerFacet?: number;
  run?: boolean;
  // can override other parameters
  params?: QueryParameters;
}

export type IPropsQueryAlgolia = IProps;

interface IAlgoliaStats {
  page?: number;
  nbPages?: number;
  nbHits?: number;
  hitsPerPage?: number;
}

export interface IOutputFacets {
  [facetName: string]: {
    [facetValue: string]: number;
  };
}

export interface IAlgoliaResponse<T> {
  hits: T[];
  stats: IAlgoliaStats;
  facets?: IOutputFacets;
  error?: Error;
  loading: boolean;
}

/**
 * Request to algolia
 */
export const useAlgoliaQuery = <T>(
  {
    indexName,
    query = "",
    facets,
    facetFilters,
    numericFilters,
    page = 0,
    hitsPerPage = 21,
    offset,
    length,
    maxValuesPerFacet,
    run = true,
    params = {},
  }: IProps,
  deps?: DependencyList
): IAlgoliaResponse<T> => {
  const initState = (): IAlgoliaResponse<T> => ({
    loading: true,
    hits: [],
    facets: {},
    stats: {},
  });

  const [response, setResponse] = useState<IAlgoliaResponse<T>>(initState);

  const { isNotMounted } = useIsMountedFunctions();

  /**
   * Call to algolia
   */
  useEffect(() => {
    if (!run) {
      return;
    }

    // Clean response before a new request
    setResponse(initState);

    const searchQuery = {
      indexName,
      query,
      params: {
        facets,
        facetFilters,
        numericFilters,
        // If offset exist ignore page and hitsPerPage
        ...(offset !== undefined ? { offset, length } : { page, hitsPerPage }),
        maxValuesPerFacet,
        ...params,
      },
    };

    algoliaClient
      .search<T>([searchQuery])
      .then((res) => {
        if (!res || isNotMounted()) {
          return;
        }
        const [result] = res.results || [];

        setResponse({
          loading: false,
          hits: result.hits,
          facets: result.facets || undefined,
          stats: {
            nbHits: result.nbHits,
            page: result.page,
            nbPages: result.nbPages,
            hitsPerPage: result.hitsPerPage,
          },
        });
      })
      .catch((error) => {
        // tslint:disable-next-line: no-console
        console.error(error);
        setResponse((res) => ({ ...res, loading: false, error }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return response;
};
